import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RefreshCw, Plus } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export const TopicManagement = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [newTopic, setNewTopic] = useState("");

  const { data: topics, isLoading } = useQuery({
    queryKey: ['weekly-topics'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('weekly_ev_topics')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10);
      
      if (error) throw error;
      return data;
    },
  });

  const regenerateTopicsMutation = useMutation({
    mutationFn: async () => {
      const response = await supabase.functions.invoke('agent-orchestrator', {
        body: { 
          action: 'REGENERATE_TOPICS'
        }
      });
      
      if (response.error) throw response.error;
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['weekly-topics'] });
      toast({
        title: "Topics Regenerated",
        description: "New topics have been generated successfully.",
      });
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to regenerate topics. Please try again.",
      });
    }
  });

  const addTopicMutation = useMutation({
    mutationFn: async (topic: string) => {
      const { data, error } = await supabase
        .from('weekly_ev_topics')
        .insert([{
          main_topic: topic,
          status: 'draft',
          week_start_date: new Date().toISOString(),
          week_end_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
        }])
        .select();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['weekly-topics'] });
      setNewTopic("");
      toast({
        title: "Topic Added",
        description: "New topic has been added successfully.",
      });
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to add topic. Please try again.",
      });
    }
  });

  const handleAddTopic = () => {
    if (!newTopic.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please enter a topic.",
      });
      return;
    }
    addTopicMutation.mutate(newTopic);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex justify-between items-center">
          <span>Topic Management</span>
          <Button
            variant="outline"
            onClick={() => regenerateTopicsMutation.mutate()}
            disabled={regenerateTopicsMutation.isPending}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${regenerateTopicsMutation.isPending ? 'animate-spin' : ''}`} />
            Regenerate Topics
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex gap-2">
            <Input
              placeholder="Enter new topic"
              value={newTopic}
              onChange={(e) => setNewTopic(e.target.value)}
            />
            <Button onClick={handleAddTopic} disabled={addTopicMutation.isPending}>
              <Plus className="h-4 w-4 mr-2" />
              Add Topic
            </Button>
          </div>

          {isLoading ? (
            <div>Loading topics...</div>
          ) : (
            <div className="space-y-2">
              {topics?.map((topic) => (
                <div
                  key={topic.id}
                  className="p-3 border rounded-lg flex justify-between items-center"
                >
                  <div>
                    <h4 className="font-medium">{topic.main_topic}</h4>
                    <p className="text-sm text-muted-foreground">
                      Status: {topic.status}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};